import React, { useState, useEffect } from 'react';
import { Avatar, Slider, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import {
  GlassModal,
  GlassModalContent,
  GlassButton,
  GlassIconButton,
  SectionHeader,
  BodyText,
} from '../../../components/glassmorphism/GlassmorphismStyles';
import Cropper from 'react-easy-crop';
import { connect } from 'react-redux';
import { UserDetailsViewModel } from '../../../configuration/api/apiSchema';
import { HandymanAppState } from '../../../configuration/redux/reducers';
import { getUserDetails, updateUserAvatar } from '../../Auth/actions';
import PreviewImageModal from '../../../components/PreviewImage/PreviewImageModal';
import { Link } from 'react-router-dom';

interface StateFromProps {
  userDetails?: UserDetailsViewModel;
  isUserLoading: boolean;
  isAvatarUpdated: boolean;
}

interface DispatchFromProps {
  updateUserAvatar: (avatarBase64: string) => void;
  getUserDetails: (userId?: string) => void;
}

interface PassedProps {
  userId?: string;
}

type Props = StateFromProps & DispatchFromProps & PassedProps;

const AvatarSection = (props: Props) => {
  const isReadonly = props.userId !== undefined;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [image, setImage] = useState<string | null>(null);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [cropAreaPixels, setCropAreaPixels] = useState<any>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState<boolean>(false);

  const [isImageModalOpen, setIsImageModalOpen] = useState<boolean>(false);
  const [base64SelectedImage, setBase64SelectedImage] = useState<string | null>(
    null,
  );

  const handleImageClick = (src: string): void => {
    setBase64SelectedImage(src);
    setIsImageModalOpen(true);
  };

  const handleCloseImageModal = (): void => {
    setIsImageModalOpen(false);
    setBase64SelectedImage(null);
  };

  useEffect(() => {
    if (props.isAvatarUpdated) {
      props.getUserDetails();
    }
  }, [props.isAvatarUpdated]);

  // to change data when user go to his own profile or browse handyman
  useEffect(() => {
    if (props.userId) {
      props.getUserDetails(props.userId);
    } else {
      props.getUserDetails();
    }
  }, [props.userId]);

  // If your API puts the user’s avatar in userDetails.avatarUrl (or just .avatar)
  // we’ll track that here:
  const avatarSrc = props.userDetails?.avatarUrl || '';
  const userName = `${props.userDetails?.firstName || ''} ${
    props.userDetails?.lastName || ''
  }`;
  // const userId = userDetails?.id || "";

  // Called when user picks a new file
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('File input changed', event.target.files);
    const file = event.target.files?.[0];
    if (file) {
      console.log('File selected:', file.name);
      setImage(URL.createObjectURL(file));
      setIsCropModalOpen(true);
    }
  };

  // Click handler for camera icon
  const handleCameraClick = () => {
    console.log('Camera icon clicked');
    const fileInput = document.querySelector(
      'input[type="file"]',
    ) as HTMLInputElement;
    if (fileInput) {
      console.log('Triggering file input click');
      fileInput.click();
    }
  };

  // Called when user finishes cropping
  const onCropComplete = (_croppedArea: any, croppedAreaPixels: any) => {
    setCropAreaPixels(croppedAreaPixels);
  };

  // Called when user presses “Save” in modal
  const handleCropComplete = async () => {
    if (image && cropAreaPixels) {
      const croppedImgBase64 = await getCroppedImg(image, crop, cropAreaPixels);
      if (croppedImgBase64) {
        // Dispatch to Redux action so it updates the server & store
        props.updateUserAvatar(croppedImgBase64);
      }
      setIsCropModalOpen(false);
    }
  };

  return (
    <>
      <PreviewImageModal
        base64SelectedImage={base64SelectedImage}
        handleCloseImageModal={handleCloseImageModal}
        isImageModalOpen={isImageModalOpen}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          padding: '0px',
          gap: '48px',
          width: '100%',
        }}
      >
        {/* Avatar Container */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0px',
            isolation: 'isolate',
            position: 'relative',
            width: '140px',
            height: '140px',
            background: isDark
              ? 'rgba(0, 92, 173, 0.7)'
              : 'rgba(0, 36, 58, 0.8)',
            boxShadow:
              '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
            borderRadius: '100px',
          }}
        >
          <Avatar
            src={avatarSrc}
            alt={userName}
            sx={{
              width: 140,
              height: 140,
              cursor: 'pointer',
              fontSize: '3rem',
              fontWeight: 600,
              backgroundColor: isDark
                ? 'rgba(0, 92, 173, 0.7)'
                : 'rgba(0, 36, 58, 0.8)',
              color: '#00FFFF',
            }}
            onClick={() => handleImageClick(avatarSrc)}
          />

          {/* Badge/Camera Container */}
          {!isReadonly && (
            <Box
              sx={{
                position: 'absolute',
                width: '34px',
                height: '34px',
                right: 0,
                bottom: 0,
                background: '#E5E5DC',
                borderRadius: '100px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '0px 6.5px',
                  width: '32px',
                  height: '32px',
                  background: '#005CAD',
                  boxShadow:
                    '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                  backdropFilter: 'blur(6px)',
                  borderRadius: '100px',
                  cursor: 'pointer',
                }}
                onClick={handleCameraClick}
              >
                <PhotoCameraIcon
                  sx={{
                    width: '16px',
                    height: '16px',
                    color: '#E5E5DC',
                    filter: 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                  }}
                />
              </Box>
            </Box>
          )}

          <input
            type='file'
            accept='image/*'
            hidden
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </Box>

        {/* User Info Container */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: '24px 0px 0px 24px',
            gap: '8px',
            flex: 1,
          }}
        >
          <Box
            sx={{
              fontFamily: 'Montserrat',
              fontStyle: 'normal',
              fontWeight: 600,
              fontSize: '34px',
              lineHeight: '123.5%',
              letterSpacing: '0.25px',
              color: isDark ? '#B6B6B6' : '#1A1A1A',
            }}
          >
            {userName || 'User Name'}
          </Box>

          <Box
            sx={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '150%',
              letterSpacing: '0.15px',
              color: isDark ? '#D5D5D5' : '#585858',
            }}
          >
            Account ID: #{props.userDetails?.id || '332441'}
          </Box>

          {!isReadonly && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                padding: '0px',
                gap: '8px',
              }}
            >
              <Box
                sx={{
                  fontFamily: 'Roboto',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '150%',
                  letterSpacing: '0.15px',
                  color: isDark ? '#D5D5D5' : '#585858',
                }}
              >
                To see a public version of your account click
              </Box>
              <Link
                to={`/browse-handyman/${props.userDetails?.id}`}
                style={{
                  fontFamily: 'Roboto',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '150%',
                  letterSpacing: '0.15px',
                  color: isDark ? '#00FFFF' : '#005CAD',
                  textDecoration: 'none',
                }}
              >
                here
              </Link>
            </Box>
          )}
        </Box>
      </Box>

      {/* Crop Modal */}
      <GlassModal
        open={isCropModalOpen && !isReadonly}
        onClose={() => setIsCropModalOpen(false)}
      >
        <GlassModalContent sx={{ width: { xs: '90%', sm: 500 } }}>
          <GlassIconButton
            onClick={() => setIsCropModalOpen(false)}
            sx={{ position: 'absolute', top: 16, right: 16 }}
          >
            <CloseIcon />
          </GlassIconButton>

          <SectionHeader sx={{ mb: 3, pr: 5 }}>
            Adjust and Crop Image
          </SectionHeader>

          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 300,
              mt: 2,
              borderRadius: '16px',
              overflow: 'hidden',
            }}
          >
            {image && (
              <Cropper
                image={image}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </Box>

          <Box sx={{ mt: 3, mb: 2 }}>
            <BodyText sx={{ mb: 1, fontSize: '14px' }}>Zoom Level</BodyText>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e, value) => setZoom(value as number)}
              sx={{
                color: '#00E8E7',
                '& .MuiSlider-thumb': {
                  backgroundColor: '#00E8E7',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 4px 8px rgba(0, 232, 231, 0.3)',
                },
                '& .MuiSlider-track': {
                  backgroundColor: '#00E8E7',
                },
                '& .MuiSlider-rail': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            />
          </Box>

          <GlassButton
            onClick={handleCropComplete}
            sx={{ mt: 2, width: '100%' }}
          >
            Save Avatar
          </GlassButton>
        </GlassModalContent>
      </GlassModal>
    </>
  );
};

/**
 * Connect to Redux
 */

const mapStateToProps = (state: HandymanAppState): StateFromProps => ({
  userDetails: state.user.userDetails,
  isUserLoading: state.user.isLoading,
  isAvatarUpdated: state.user.isAvatarUpdated,
});

const mapDispatchToProps: DispatchFromProps = {
  updateUserAvatar,
  getUserDetails,
};

export default connect<
  StateFromProps,
  DispatchFromProps,
  PassedProps,
  HandymanAppState
>(
  mapStateToProps,
  mapDispatchToProps,
)(AvatarSection);

// Optionally rename to getCroppedImgBase64
export const getCroppedImg = async (
  imageSrc: string,
  crop: { x: number; y: number },
  cropAreaPixels: any,
): Promise<string | null> => {
  const image = new Image();
  image.src = imageSrc;

  await new Promise((resolve) => {
    image.onload = resolve;
  });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  canvas.width = cropAreaPixels.width;
  canvas.height = cropAreaPixels.height;

  ctx.drawImage(
    image,
    cropAreaPixels.x,
    cropAreaPixels.y,
    cropAreaPixels.width,
    cropAreaPixels.height,
    0,
    0,
    cropAreaPixels.width,
    cropAreaPixels.height,
  );

  // Return a base64 string
  return canvas.toDataURL('image/jpeg');
};
